import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiButton from '@mui/material/Button';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants';
import { AddCircleIcons, RefreshListIcons } from '../../../icons/icons';
import {
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  MainLayout,
} from '../../../ui';
import { Modal } from '../../../ui/modal/modal';
import {
  deleteEmployee,
  getEmployee,
} from '../../../utils/api_service/employees';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import { fetchOrganizationUsers } from '../../../utils/redux_store/features/organization_user_reducer';
import { store } from '../../../utils/redux_store/store';
import { EmployeeUser } from '../../../utils/types/admin_types';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { getColumns } from './Functions';
import { getMenuItemsForMenu } from './Functions/GetContextMenuItems';
import { useEmployeeManagement } from './Hooks';
import { BulkUploadModal } from './Modals/BulkUploadModal';
import { CreateEmployeeModal } from './Modals/CreateEmployeeModal';
import { EditEmployeeModal } from './Modals/EditEmployeeModal';

export const EmployeeManagement: React.FC = () => {
  const { t } = useTranslation(NS.adminPages);
  const {
    employeeUserList,
    loading,
    pageNumber,
    totalPages,
    userGroup,
    editedEmployee,
    editModalVisible,
    setEditModalVisible,
    inviteModalVisible,
    setInviteModalVisible,
    inviteEmployee,
    inviteBulkEmployees,
    bulkUploadModalVisible,
    setBulkUploadModalVisible,
    jobTitleList,
    onAddNewJobTitle,
    organizationUnitList,
    onPageChange,
    editUser,
    userRole,
    handleSearchTermChange,
    singleEmployee,
    setSingleEmployee,
    setEditedEmployee,
    setEmployeeUserList,
    totalRecords,
  } = useEmployeeManagement();

  // Context menu state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<EmployeeUser | null>(null);
  const [deleteConfirmState, setDeleteConfirmState] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] =
    useState<EmployeeUser | null>(null);

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    try {
      const response = await deleteEmployee(employeeToDelete.id);
      if (response.ok) {
        const updatedEmployees = employeeUserList.filter(
          (employee) => employee.id !== employeeToDelete.id,
        );
        setEmployeeUserList(updatedEmployees);
        toast.success('Employee deleted.', { position: 'bottom-center' });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting employee: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setDeleteConfirmState(false);
      setEmployeeToDelete(null);
    }
  };

  // Open context menu for row
  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: EmployeeUser,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  // Close context menu
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openEditModal = async (employee: EmployeeUser) => {
    const response = await getEmployee(employee.id);
    setSingleEmployee(response);
    setEditedEmployee(employee);
    setEditModalVisible(true);
  };

  const openDeleteDialog = (employee: EmployeeUser) => {
    setEmployeeToDelete(employee);
    setDeleteConfirmState(true);
  };

  // Menu items with permissions
  const contextMenuItems = getMenuItemsForMenu({
    onEdit: (row: EmployeeUser) => {
      openEditModal(row);
    },
    onDelete: (row: EmployeeUser) => {
      openDeleteDialog(row);
    },
    t,
  });

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SettingsSubNav currentPath='/employee-management' />
      <MainLayout>
        {/* Search & Actions Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 400 } }}>
            <FilterSearch
              onSearch={handleSearchTermChange}
              searchText={t(ADMIN_PAGES_KEYS.employeeManagement.searchPlaceholder)}
            />
          </Box>
          <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap'>
            <MuiButton
              variant='outlined'
              startIcon={<RefreshListIcons style={{ width: 16, height: 16 }} />}
              onClick={() => store.dispatch(fetchOrganizationUsers())}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.secondary',
                borderColor: 'divider',
                px: 2,
                py: 0.85,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'action.active',
                },
              }}
            >
              {t(ADMIN_PAGES_KEYS.employeeManagement.refresh)}
            </MuiButton>
            <MuiButton
              variant='outlined'
              startIcon={<AddCircleIcons style={{ width: 16, height: 16 }} />}
              onClick={() => setBulkUploadModalVisible(true)}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.secondary',
                borderColor: 'divider',
                px: 2,
                py: 0.85,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'action.active',
                },
              }}
            >
              {t(ADMIN_PAGES_KEYS.employeeManagement.inviteBulk)}
            </MuiButton>
            <PermissionGate requiredPermissions={[Permission.USERS_CREATE]}>
              <MuiButton
                variant='contained'
                startIcon={<AddCircleIcons style={{ width: 16, height: 16 }} />}
                onClick={() => setInviteModalVisible(true)}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  bgcolor: '#093725',
                  color: '#ffffff',
                  px: 2.25,
                  py: 0.85,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#062619',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                {t(ADMIN_PAGES_KEYS.employeeManagement.inviteUsers)}
              </MuiButton>
            </PermissionGate>
          </Stack>
        </Box>

        {/* Table Container */}
        <Paper
          variant='outlined'
          sx={{
            borderRadius: '6px',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            width: '100%',
            minHeight: 360,
          }}
        >
          <DataTable<EmployeeUser>
            isLoading={loading}
            columns={getColumns({
              handleOpenMenu,
              handleCloseMenu,
              anchorEl,
              t,
            })}
            data={employeeUserList}
            rowsPerPage={10}
            onPageChange={onPageChange}
            currentPage={pageNumber}
            totalPages={totalPages}
            totalRecords={totalRecords}
          />
        </Paper>
      </MainLayout>

      <Dialog
        title='Are you sure you want to delete this employee?'
        handleApply={handleDeleteEmployee}
        confirmButtonLable='Yes, Delete'
        isOpen={deleteConfirmState}
        onClose={() => setDeleteConfirmState(false)}
        cancelButtonLable='Cancel'
        dialogType={DIALOG_TYPE.ERROR}
        justify='end'
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          This action cannot be undone, changes that you have made will be lost.
        </Typography>
      </Dialog>

      <Modal
        visible={inviteModalVisible}
        modalContent={
          <CreateEmployeeModal
            employeeUsers={employeeUserList}
            organizationUnits={organizationUnitList}
            jobTitles={jobTitleList}
            addNewJobTitle={onAddNewJobTitle}
            createEmployee={inviteEmployee}
          />
        }
        toggle={() => setInviteModalVisible(false)}
        headerText={t(ADMIN_PAGES_KEYS.employeeManagement.inviteModal.title)}
      />

      <Modal
        visible={editModalVisible}
        modalContent={
          <EditEmployeeModal
            employeeUsers={employeeUserList}
            organizationUnits={organizationUnitList}
            jobTitles={jobTitleList}
            userGroupList={userGroup}
            userRoleList={userRole}
            singleEmployee={singleEmployee}
            employee={editedEmployee || ({} as EmployeeUser)}
            addNewJobTitle={onAddNewJobTitle}
            editUser={editUser}
          />
        }
        toggle={() => setEditModalVisible(false)}
        headerText='Edit Employee Details'
      />

      <Modal
        visible={bulkUploadModalVisible}
        modalContent={
          <BulkUploadModal inviteEmployeesFromCsv={inviteBulkEmployees} />
        }
        toggle={() => setBulkUploadModalVisible(false)}
        headerText='Bulk Invite Employees'
      />

      {/* Context Menu */}
      {anchorEl && selectedRow && (
        <ContextMenu<EmployeeUser>
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          row={selectedRow}
          menuItems={contextMenuItems}
        />
      )}
    </Box>
  );
};
